import * as React from 'react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { useApi } from '../../../design-system/useApi';
import { hrApi } from '../api';
import type { ExpectedShift, ShiftOverride, ShiftOverrideKind } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  employeeId: string;
  employeeName: string;
  date: string;
  /** Whether this employee has approved leave on this date (from the week grid). */
  onLeave: boolean;
  /** The resolved cell being edited — used to prefill and to know whether an
   *  override already exists here (source === 'override'). */
  current: ExpectedShift;
  onSaved: () => void;
}

const KIND_LABELS: Record<ShiftOverrideKind, string> = {
  custom_hours: 'Custom hours',
  day_off: 'Day off',
  extra_day: 'Extra day',
};

/** Custom hours / day off / clear override for one employee on one date.
 *  Per-cell action from the Schedule grid — only rendered for users holding
 *  hr.schedule.manage. */
export function OverrideDialog({ open, onOpenChange, employeeId, employeeName, date, onLeave, current, onSaved }: Props) {
  const confirm = useConfirm();
  const hasExistingOverride = current.source === 'override';

  // The week grid gives us the resolved cell, not the raw override row (no id).
  // Fetch the raw row only when one exists, so "Clear override" has an id to delete.
  const overridesQ = useApi<{ overrides: ShiftOverride[] }>(
    open && hasExistingOverride
      ? `/api/hr/schedule/overrides?from=${date}&to=${date}&employee_id=${employeeId}`
      : null,
    [open, hasExistingOverride, date, employeeId],
  );
  const existing = overridesQ.data?.overrides.find((o) => o.date === date);

  const [kind, setKind] = React.useState<ShiftOverrideKind>('custom_hours');
  const [start, setStart] = React.useState('09:00');
  const [end, setEnd] = React.useState('17:00');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setKind('custom_hours');
    setStart(current.start ?? '09:00');
    setEnd(current.end ?? '17:00');
    setNote('');
  }, [open, current]);

  // Once the raw override row loads (existing override case), replace the
  // defaults with its real values.
  React.useEffect(() => {
    if (!existing) return;
    setKind(existing.kind);
    if (existing.start_time) setStart(existing.start_time.slice(0, 5));
    if (existing.end_time) setEnd(existing.end_time.slice(0, 5));
    setNote(existing.note ?? '');
  }, [existing]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (kind !== 'day_off' && (!start || !end)) {
      toast.error('Set both a start and end time');
      return;
    }
    setBusy(true);
    try {
      await hrApi.upsertShiftOverride({
        employee_id: employeeId,
        date,
        kind,
        start_time: kind === 'day_off' ? null : start,
        end_time: kind === 'day_off' ? null : end,
        note: note.trim() || null,
      });
      toast.success('Override saved');
      onSaved();
    } catch (err: any) {
      toast.error('Could not save override', { description: err.message });
    } finally { setBusy(false); }
  }

  async function clear() {
    if (!existing) return;
    if (!(await confirm({
      title: 'Clear this override?',
      description: 'The day reverts to the assigned template or default hours.',
      destructive: true,
      confirmLabel: 'Clear',
    }))) return;
    setBusy(true);
    try {
      await hrApi.deleteShiftOverride(existing.id);
      toast.success('Override cleared');
      onSaved();
    } catch (err: any) {
      toast.error('Could not clear override', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Override — {employeeName}, {date}</DialogTitle>
        </DialogHeader>

        {onLeave && (
          <div className="flex items-start gap-2.5 rounded-xl bg-coral/10 ring-1 ring-coral/25 px-3.5 py-3 text-[12.5px] text-coral-700 dark:bg-coral/15 dark:text-coral-200 dark:ring-coral/30">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              This employee has approved leave on this date. Approved leave takes precedence over
              overrides — this won't take effect unless the leave is cancelled or denied.
            </span>
          </div>
        )}

        <form onSubmit={save} className="grid gap-4 pt-1">
          <div className="grid gap-1.5">
            <Label>Type</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as ShiftOverrideKind)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(KIND_LABELS) as ShiftOverrideKind[]).map((k) => (
                  <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {kind !== 'day_off' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Start</Label>
                  <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} required />
                </div>
                <div className="grid gap-1.5">
                  <Label>End</Label>
                  <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} required />
                </div>
              </div>
              {end < start && (
                <p className="text-[12px] text-muted-foreground -mt-2">Crosses midnight — ends the next day.</p>
              )}
            </>
          )}

          <div className="grid gap-1.5">
            <Label>Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            {existing ? (
              <Button type="button" variant="ghost" disabled={busy} onClick={clear} className="text-muted-foreground hover:text-destructive">
                Clear override
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
